import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import { Helmet } from 'react-helmet-async';
import Spinner from '@/components/Spinner';
import {
  InvestorProfileCard,
  InvestmentTab,
  TransactionsTab,
  MembersCommissionTab,
  DocumentsTab,
} from '@/components/Investors';
import { CommissionRecord } from '@/components/Investors/MembersCommissionTab';
import { Transaction } from '@/components/Investors/TransactionsTab';
import { userInvestmentPackageStats } from '@/api/Admin/userPackage';
import { UserInvestmentPackageStatsAPI, InvestmentPackage } from '@/api/Admin/type';
import dayjs from 'dayjs';

export interface InvestorInfo {
  id: string;
  code: string;
  fullname: string;
  email: string;
  phone: string;
  avatar_url?: string;
  gender: number | null;
  birthday: string | null;
  job_title: string | null;
  identifier: string;
  referral_code: string;
  status: 'Active' | 'Inactive';
}

interface TabConfig {
  id: string;
  label: string;
}

const VALID_TABS = ['investment', 'transactions', 'members', 'documents'];

const InvestorDetail: React.FC = () => {
  const { t } = useTranslation();
  const { investorId } = useParams<{ investorId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [investor, setInvestor] = useState<InvestorInfo | null>(null);
  const [investmentStats, setInvestmentStats] = useState<{
    totalPackages: number;
    totalInvestment: number;
    totalProfit: number;
    total_overdue: number;
  } | undefined>(undefined);
  const [investments, setInvestments] = useState<InvestmentPackage[]>([]);
  const [transactions, setTransactions] = useState<Transaction[] | undefined>(undefined);
  const [transactionStats, setTransactionStats] = useState<{
    totalIn: number;
    totalOut: number;
    totalTransactions: number;
  } | undefined>(undefined);
  const [commissionData, setCommissionData] = useState<{
    stats: { totalCommission: number; totalPaid: number; totalPending: number; totalRejected: number };
    records: CommissionRecord[];
  } | undefined>(undefined);
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Pagination states
  const [transPage, setTransPage] = useState(1);
  const [transTotal, setTransTotal] = useState(0);

  const [commPage, setCommPage] = useState(1);
  const [commTotal, setCommTotal] = useState(0);

  const [docPage, setDocPage] = useState(1);
  const [docTotal, setDocTotal] = useState(0);

  const tabFromUrl = searchParams.get('tab');
  const activeTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'investment';

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    const fetchInvestor = async () => {
      setLoading(true);
      try {
        if (investorId) {
          await userInvestmentPackageStats(investorId, 1, transPage, commPage, docPage).then((response: UserInvestmentPackageStatsAPI) => {
            if (response.data && response.status === true) {
              const userInfo = response.data.user_info;
              setInvestor({
                id: String(userInfo.id),
                code: 'userInfo.code',
                fullname: userInfo.fullname,
                email: userInfo.email,
                phone: userInfo.phone || '',
                avatar_url: userInfo.avatar_url || undefined,
                gender: userInfo.gender,
                birthday: userInfo.birthday,
                job_title: userInfo.job_title,
                identifier: userInfo.cccd_number || '',
                referral_code: userInfo.referral_code || '',
                status: userInfo.is_active === 1 ? 'Active' : 'Inactive'
              });

              // Investrment Stats
              if (response.data.investment) {
                setInvestmentStats({
                  totalPackages: response.data.investment.total_packages,
                  totalInvestment: response.data.investment.total_invested,
                  totalProfit: response.data.investment.total_paid_profit,
                  total_overdue: response.data.investment.total_overdue || 0
                });

                if (response.data.investment.history) {
                  const historyData = Array.isArray(response.data.investment.history)
                    ? response.data.investment.history
                    : ((response.data.investment.history as any)?.data || []);

                  if (Array.isArray(historyData)) {
                    const mappedInvestments: InvestmentPackage[] = historyData.map((hist: any, index: number) => {
                      let profits: any[] = [];
                      if (Array.isArray(hist.profits)) {
                        profits = hist.profits;
                      } else if (hist.profits?.data && Array.isArray(hist.profits.data)) {
                        profits = hist.profits.data;
                      } else if (hist.profits && typeof hist.profits === 'object') {
                        // Handle case where profits is an object map or has unexpected structure
                        // Avoid picking up pagination metadata fields which are usually primitives
                        const values = Object.values(hist.profits);
                        const objectValues = values.filter((v: any) => v && typeof v === 'object' && !Array.isArray(v));
                        if (objectValues.length > 0) {
                          profits = objectValues;
                        }
                      }

                      // Calculate progress
                      const paidProfits = profits.filter((p: any) =>
                        ['paid', 'approved', 'succeed', 'completed'].includes(p.status?.toLowerCase())
                      );
                      const received = paidProfits.reduce((acc: number, curr: any) => acc + Number(curr.amount || 0), 0);
                      const invested = Number(hist.total_invested || 0);
                      const userPackageId = Number(hist.user_package_id ?? hist.id ?? index + 1);

                      return {
                        id: userPackageId,
                        package_id: Number(hist.package_id || 0),
                        package_name: hist.package_name,
                        status_label: hist.status,
                        status_code: hist.status,
                        buy_date: hist.created_at,
                        total_invested: invested,
                        total_months: hist.total_months,
                        monthly_profit: hist.monthly_profit,
                        interest_rate: '0',
                        progress: hist.progress,
                        payment_history: hist.payments || [],
                        referrer_id: hist.referrer_id ? hist.referrer_id : undefined,
                        profits_history: profits.map((p: any) => ({
                          ...p,
                          period: p.period_index, // Map API period_index to UI expected period
                          dueDate: p.due_date,    // Map API due_date to UI expected dueDate
                          paidDate: p.paid_at,    // Map API paid_at to UI expected paidDate
                          amount: p.net_amount    // Map API net_amount to UI expected amount/net_amount
                        }))
                      };
                    });
                    setInvestments(mappedInvestments);
                  }
                }
              }

              // Map Transactions
              if (response.data.transaction && response.data.transaction.logs) {
                const logsData = response.data.transaction.logs.data || [];
                const total = response.data.transaction.logs.total || 0;
                setTransTotal(total);
                setTransactionStats({
                  totalIn: response.data.transaction.money_in,
                  totalOut: response.data.transaction.money_out,
                  totalTransactions: response.data.transaction.count
                });

                const txs: Transaction[] = logsData.map((log) => {
                  return {
                    id: String(log.id),
                    code: log.payment_code,
                    time: dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss'),
                    type: log.transaction_type as Transaction['type'], // Assuming API returns 'deposit', 'withdraw', 'profit', or 'commission'
                    amount: Number(log.amount),
                    status: log.status === 'success' || log.status === 'succeed' ? 'completed' : (log.status === 'pending' ? 'pending' : 'failed'),
                  };
                });
                setTransactions(txs);
              }

              // Map Commission
              if (response.data.commission) {
                const comm = response.data.commission;
                const logsData = comm.logs?.data || [];
                const total = comm.logs?.total || 0;
                setCommTotal(total);

                const records: CommissionRecord[] = logsData.map(log => ({
                  id: String(log.id),
                  code: log.user_id.toString(),
                  partnerName: log.user_info?.fullname || 'N/A',
                  referrer: response.data?.user_info.fullname || 'N/A',
                  contractCode: log.package_name_snapshot,
                  investAmount: Number(log.price_snapshot),
                  commissionRate: Number(log.commission_rate_snapshot),
                  commissionAmount: Number(log.received_amount),
                  status: log.status
                }));

                setCommissionData({
                  stats: {
                    totalCommission: comm.total_commission || 0,
                    totalPaid: comm.paid_commission || 0,
                    totalPending: comm.pending_commission || 0,
                    totalRejected: 0
                  },
                  records: records
                });
              } else {
                setCommissionData({
                  stats: { totalCommission: 0, totalPaid: 0, totalPending: 0, totalRejected: 0 },
                  records: []
                });
              }

              // Documents
              if (response.data.documents && response.data.documents.data) {
                setDocs(response.data.documents.data);
                setDocTotal(response.data.documents.total || 0);
              }
            }
          });
        }
      } catch (error) {
        console.error('Error fetching investor:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestor();
  }, [investorId, transPage, commPage, docPage]);

  const tabs: TabConfig[] = [
    { id: 'investment', label: t('investorDetail.tabs.investment', 'Đầu tư') },
    { id: 'transactions', label: t('investorDetail.tabs.transactions', 'Giao dịch') },
    { id: 'members', label: t('investorDetail.tabs.membersCommission', 'Thành viên & hoa hồng') },
    { id: 'documents', label: t('investorDetail.tabs.documents', 'Tài liệu') },
  ];

  const breadcrumbs: BreadcrumbItem[] = [
    { label: t('nav.dashboard', 'Dashboard'), path: '/admin/dashboard' },
    { label: t('investors.title', 'Quản lý nhà đầu tư'), path: '/admin/investors' },
    { label: investor?.fullname || t('investorDetail.title', 'Chi tiết nhà đầu tư') },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'investment':
        return <InvestmentTab stats={investmentStats} investments={investments} loading={loading} />;
      case 'transactions':
        return <TransactionsTab
          transactions={transactions}
          stats={transactionStats}
          currentPage={transPage}
          totalItems={transTotal}
          onPageChange={setTransPage}
        />;
      case 'members':
        return <MembersCommissionTab
          stats={commissionData?.stats}
          records={commissionData?.records}
          currentPage={commPage}
          totalItems={commTotal}
          onPageChange={setCommPage}
        />;
      case 'documents':
        return <DocumentsTab
          userId={Number(investor?.id) || 0}
          docs={docs}
          currentPage={docPage}
          totalItems={docTotal}
          onPageChange={setDocPage}
        />;
      default:
        return <InvestmentTab stats={investmentStats} investments={investments} loading={loading} />;
    }
  };

  if (loading && !investor) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Spinner />
      </div>
    );
  }

  if (!investor && !loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <p className="text-gray-500 dark:text-gray-400">
          {t('investorDetail.notFound', 'Không tìm thấy nhà đầu tư')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Helmet>
        <title>{t('investorDetail.title', 'Chi tiết nhà đầu tư')} | BEQ Reward Admin</title>
      </Helmet>

      <PageHeader title={investor?.fullname || ''} breadcrumbs={breadcrumbs} />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column - Profile Card */}
        <div className="w-full lg:w-[360px] flex-shrink-0">
          {investor && <InvestorProfileCard investor={investor} />}
        </div>

        {/* Right Column - Tabs */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
              <nav className="flex min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === tab.id
                      ? 'text-primary! dark:text-primary-light!'
                      : 'text-gray-500! dark:text-gray-400! hover:text-gray-700! dark:hover:text-gray-300!'
                      }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary dark:bg-primary-light" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center p-10"><Spinner /></div>
              ) : (
                renderTabContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorDetail;
